import { useEffect } from 'react';
import { useBulkStore, type BulkSection } from '../stores/bulkStore';

export interface UseKeyboardNavOptions<T extends { id: string }> {
  list: T[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  section: BulkSection;
  onPaletteToggle?: () => void;
  onEscape?: () => void;
  enabled?: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardNav<T extends { id: string }>({
  list,
  selectedId,
  setSelectedId,
  section,
  onPaletteToggle,
  onEscape,
  enabled = true,
}: UseKeyboardNavOptions<T>): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof document === 'undefined') return;

    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        onPaletteToggle?.();
        return;
      }

      if (e.key === 'Escape') {
        useBulkStore.getState().clear(section);
        onEscape?.();
        return;
      }

      if (isTypingTarget(e.target)) return;
      if (list.length === 0) return;

      const idx = list.findIndex(it => it.id === selectedId);

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIdx = idx < 0 ? 0 : Math.min(list.length - 1, idx + 1);
        const next = list[nextIdx];
        if (next) setSelectedId(next.id);
        return;
      }

      if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIdx = idx < 0 ? 0 : Math.max(0, idx - 1);
        const prev = list[prevIdx];
        if (prev) setSelectedId(prev.id);
        return;
      }

      if (e.key === 'x' || e.key === 'X') {
        e.preventDefault();
        const cur = idx >= 0 ? list[idx] : null;
        if (cur) useBulkStore.getState().toggle(section, cur.id);
        return;
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [
    list,
    selectedId,
    setSelectedId,
    section,
    onPaletteToggle,
    onEscape,
    enabled,
  ]);
}

export default useKeyboardNav;
